import type { ProspectSearchSession, ResearchCompany, ResearchContact } from '../types';

const INTERNAL_REFERENCE_PATTERNS = [
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
  /\b(?:run|payload|billing|session|evt|msg|pi|cus)_[a-z0-9_-]{3,}\b/gi,
  /\bsk-(?:live|test)-[a-z0-9_-]{3,}\b/gi,
  /\b(?:tok|token|secret)_[a-z0-9_-]{3,}\b/gi,
  /\b(?:db|id|session|record|row)\s*[:= ]\s*c[a-z0-9]{20,}\b/gi,
];

export function redactUserFacingText(value: unknown): string {
  if (typeof value !== 'string') return '';

  return INTERNAL_REFERENCE_PATTERNS.reduce(
    (text, pattern) => text.replace(pattern, '[hidden reference]'),
    value,
  );
}

export function getSafeUserMessage(error: unknown): string {
  const status = getErrorStatus(error);

  if (status === 401 || status === 403) {
    return 'You do not have access to that prospect context for this organization.';
  }

  if (status === 404) {
    return 'That prospect context is no longer available. You can rerun the search from chat.';
  }

  if (status === 503 || status === 504) {
    return 'Prospect research is temporarily unavailable. Please try again in a moment.';
  }

  const message = getErrorMessage(error);
  return redactUserFacingText(message) || 'Something went wrong while loading prospect research.';
}

export function sanitizeResearchContact(contact: ResearchContact): ResearchContact {
  return {
    ...contact,
    fullName: redactUserFacingText(contact.fullName),
    jobTitle: redactNullable(contact.jobTitle),
    headline: redactNullable(contact.headline),
    location: redactNullable(contact.location),
  };
}

export function sanitizeResearchCompany(company: ResearchCompany): ResearchCompany {
  return {
    ...company,
    name: redactUserFacingText(company.name),
    industry: redactOptional(company.industry),
    subIndustry: redactOptional(company.subIndustry),
    description: redactOptional(company.description),
    location: redactOptional(company.location),
    country: redactOptional(company.country),
    city: redactOptional(company.city),
    contacts: company.contacts?.map(sanitizeResearchContact),
  };
}

export function sanitizeProspectSearchSession(
  session: ProspectSearchSession,
): ProspectSearchSession {
  return {
    ...session,
    errorMessage: session.errorMessage
      ? getSafeUserMessage({ message: session.errorMessage })
      : undefined,
    companies: session.companies?.map(sanitizeResearchCompany),
  };
}

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  if ('status' in error && typeof error.status === 'number') return error.status;
  if ('response' in error) {
    const response = (error as { response?: unknown }).response;
    if (response && typeof response === 'object' && 'status' in response) {
      const status = (response as { status?: unknown }).status;
      return typeof status === 'number' ? status : undefined;
    }
  }
  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== 'object') return '';

  if ('message' in error && typeof error.message === 'string') return error.message;
  if ('response' in error) {
    const response = (error as { response?: unknown }).response;
    if (response && typeof response === 'object' && 'data' in response) {
      const data = (response as { data?: unknown }).data;
      if (data && typeof data === 'object' && 'message' in data) {
        const message = (data as { message?: unknown }).message;
        return typeof message === 'string' ? message : '';
      }
    }
  }

  return '';
}

function redactOptional(value: string | undefined): string | undefined {
  return value ? redactUserFacingText(value) : undefined;
}

/** Contact fields arrive as `null` rather than absent, so null must survive redaction. */
function redactNullable(value: string | null | undefined): string | null | undefined {
  if (value === null || value === undefined) return value;
  return redactUserFacingText(value);
}
